import { createFileRoute } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";

import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PageHeader } from "@/components/PageHeader";

export const Route = createFileRoute("/_app/settings/")({ component: SettingsPage });

const passwordSchema = z.object({
  current_password: z.string().min(1),
  new_password: z.string().min(10).max(256),
  confirm: z.string(),
}).refine((d) => d.new_password === d.confirm, { message: "Passwords don't match", path: ["confirm"] });
type PasswordValues = z.infer<typeof passwordSchema>;

function SettingsPage() {
  const { user, can } = useAuth();

  const changePassword = useMutation({
    mutationFn: (v: { current_password: string; new_password: string }) => api.post("/auth/change-password", v),
    onSuccess: () => toast.success("Password updated. You'll be signed out of other sessions."),
    onError: (e) => toast.error(e instanceof Error ? e.message : "Password change failed"),
  });

  const rebuild = useMutation({
    mutationFn: () => api.post("/admin/revalidate"),
    onSuccess: () => toast.success("Site rebuild triggered"),
    onError: (e) => toast.error(e instanceof Error ? e.message : "Rebuild failed"),
  });

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<PasswordValues>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { current_password: "", new_password: "", confirm: "" },
  });

  return (
    <div className="max-w-2xl space-y-4">
      <PageHeader
        title="Settings"
        description="Account preferences and platform actions."
      />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Profile</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div><span className="text-muted-foreground">Name:</span> {user?.name}</div>
          <div><span className="text-muted-foreground">Email:</span> {user?.email}</div>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-muted-foreground">Roles:</span>
            {user?.roles.map((r) => <span key={r} className="rounded bg-secondary px-2 py-0.5 text-xs">{r}</span>)}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Change password</CardTitle>
        </CardHeader>
        <CardContent>
          <form className="space-y-3" onSubmit={handleSubmit((v) => changePassword.mutateAsync({
            current_password: v.current_password, new_password: v.new_password,
          }).then(() => reset()))}>
            <div className="space-y-1.5">
              <Label htmlFor="curr">Current password</Label>
              <Input id="curr" type="password" autoComplete="current-password" {...register("current_password")} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new">New password</Label>
              <Input id="new" type="password" autoComplete="new-password" {...register("new_password")} />
              {errors.new_password && <p className="text-xs text-destructive">{errors.new_password.message}</p>}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="conf">Confirm new password</Label>
              <Input id="conf" type="password" autoComplete="new-password" {...register("confirm")} />
              {errors.confirm && <p className="text-xs text-destructive">{errors.confirm.message}</p>}
            </div>
            <p className="text-xs text-muted-foreground">All other sessions will be signed out after changing your password.</p>
            <Button type="submit" disabled={isSubmitting || changePassword.isPending}>
              {changePassword.isPending ? "Updating…" : "Update password"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {can("revalidate", "trigger") && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Public site</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">
              The Astro site rebuilds when content is published. If something looks stale, trigger a manual rebuild.
            </p>
            <Button variant="outline" onClick={() => rebuild.mutate()} disabled={rebuild.isPending}>
              <RefreshCw className="h-4 w-4" /> {rebuild.isPending ? "Triggering…" : "Trigger site rebuild"}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
